import { SessionPlan } from "@/components/ai/session/session-plan-model";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useState } from "react";

function keyForSessionPlan(sessionId: string): string {
    return `session-plan-${sessionId}`;
}

export function useSessionPlan(sessionId: string): SessionPlan | undefined {
    const { storedValue } = useLocalStorage(keyForSessionPlan(sessionId), "");

    if (!storedValue) {
        return undefined;
    }

    return SessionPlan.fromXML(storedValue);
}

export function useSaveSessionPlan(sessionId?: string) {
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { setValue } = useLocalStorage(keyForSessionPlan(sessionId ?? ""), "");

    return {
        isPending,
        error,
        mutateAsync: (plan?: SessionPlan) => {
            if (!sessionId) {
                console.warn("Tried to save a plan without a session ID");
                return;
            }
            try {
                setIsPending(true);
                setValue(plan?.toXML() ?? "");
                setIsPending(false);
            } catch (error) {
                setIsPending(false);
                if (error instanceof Error) {
                    setError(error);
                } else {
                    setError(new Error(String(error)));
                }
            }
            return Promise.resolve();
        },
    };
}

export function useDeleteSessionPlan(sessionId: string) {
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { setValue } = useLocalStorage(keyForSessionPlan(sessionId), "");

    return {
        isPending,
        error,
        mutateAsync: (plan: SessionPlan) => {
            try {
                setIsPending(true);
                setValue(undefined);
                plan.steps.forEach((step) => {
                    step.delete(sessionId);
                });
                setIsPending(false);
            } catch (error) {
                setIsPending(false);
                if (error instanceof Error) {
                    setError(error);
                } else {
                    setError(new Error(String(error)));
                }
            }
            return Promise.resolve();
        },
    };
}
